/**
 * WONE-612: server-driven resend timer row.
 *
 * The cooldown comes from the OTP request response (``cooldown_seconds``)
 * so duplicate Customer.io dispatches stay deduped server-side. The
 * client only ticks the countdown — it never decides "Resend should
 * be available" on its own.
 *
 * Mobile parity: ``BrandedResendCodeRow`` on
 * ``velislav/WONE-611-otp-longpress-paste-only`` (PR #828) — swap-arrows
 * icon on the left, whole row dims when disabled (cooldown active or
 * resend in flight), copy "Didn't receive a code? Resend (Xs)".
 */

import React, { useEffect, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "lib/i18n/use-translation";
import { SwapArrowsIcon } from "./icons";
import styles from "./resend-row.module.scss";

export interface ResendRowProps {
  /** Seconds remaining; ``null`` until the server has responded. */
  cooldownSeconds: number | null;
  /** Currently sending the resend request. */
  resending: boolean;
  onResend: () => void;
  className?: string;
}

export const ResendRow: React.FC<ResendRowProps> = ({
  cooldownSeconds,
  resending,
  onResend,
  className,
}) => {
  const { t } = useTranslation(["auth"]);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(
    cooldownSeconds
  );

  // Re-seed whenever the parent passes a new cooldown (e.g. after the
  // user taps Resend and the server returns a fresh window).
  useEffect(() => {
    setSecondsLeft(cooldownSeconds);
  }, [cooldownSeconds]);

  // Single interval for the lifetime of an active cooldown. The
  // functional updater reads the latest value, so we don't have to
  // depend on ``secondsLeft`` (which would tear down + recreate the
  // interval every tick). The interval runs while ``cooldownSeconds``
  // describes a non-expired window and clears itself once it reaches 0.
  useEffect(() => {
    if (cooldownSeconds === null || cooldownSeconds <= 0) return;
    const handle = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null || prev <= 0) return prev;
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(handle);
  }, [cooldownSeconds]);

  const ready = secondsLeft !== null && secondsLeft <= 0 && !resending;
  const disabledRow = !ready;
  const label = (() => {
    if (resending) return t("auth:otp.resending");
    if (secondsLeft === null) return t("auth:otp.resend");
    if (secondsLeft > 0)
      return t("auth:otp.resendCountdown", { seconds: secondsLeft });
    return t("auth:otp.resend");
  })();

  return (
    <p
      className={clsx(
        styles["row"],
        disabledRow && styles["row-disabled"],
        className
      )}
    >
      <span className={styles["icon"]} aria-hidden>
        <SwapArrowsIcon size={16} />
      </span>
      <span className={styles["text"]}>
        {t("auth:otp.resendPrompt")}
        <button
          type="button"
          className={styles["button"]}
          onClick={onResend}
          disabled={!ready}
        >
          {label}
        </button>
      </span>
    </p>
  );
};
